import React from "react";
import "../../staticStyles/reactGrid.css";
import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Button, Box, IconButton, Menu, MenuItem, ListItemText, ListItemIcon, useTheme, useMediaQuery, Divider, Tooltip } from '@mui/material';
import { PostAdd, Download, Refresh, ControlCamera, IosShare, Edit, DeleteForever, CloudDownload, QueryStats } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import exportAsImage from "../../utils/exportAsImage";
import AddWidgetDialog from "components/widgetParts/AddWidgetDialog";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import { Widget, WidgetQuery } from "system/widget/Widget";
import { useDashboard } from "routes/dashboard/Dashboard";
import { ResourceActionDialog } from "components/adminResources/ResourceActionDialog";
import DashboardEditDialog from "components/widgetParts/DashboardEditDialog";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { currentUser } from 'system/InfegyUser';
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from "system/APIRequest";
import WidgetMultiqueryDialog from "components/widgetParts/WidgetMultiqueryDialog";

export interface DashboardOptionButtonsOptions {
  imageExportRef: any;
  isInLayoutMode: boolean;
  setIsInLayoutMode: (isInLayoutMode: boolean) => void;
  onAddWidget: (type:string, widgetOptions?:any, startingLayouts?:any, openSettingsOnAdd?: boolean) => void;
  onClearWidgets: () => void;
  widgets: Array<Widget>
  onRefreshWidgets: () => void;

  onUpdateWidget: (widgetId: string, updatedWidgetInfo: any, resetPage?: boolean) => Promise<any>;
}

export default function DashboardOptionButtons(props: DashboardOptionButtonsOptions): JSX.Element {
  const user = currentUser();
  const { enqueueSnackbar } = useSnackbar();
  const { i18n } = useTranslation();
  const dashboard = useDashboard();
  const theme = useTheme();
  const navigate = useNavigate();
  const { widgets, onUpdateWidget } = props;
  const { dashboardId, queryIds } = useParams();

  const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [shareDashboardDialogIsOpen, setShareDashboardDialogIsOpen] = useState<boolean>(false);
  const [editDashboardDialogIsOpen, setEditDashboardDialogIsOpen] = useState<boolean>(false);
  const [bulkWidgetQueryDialogIsOpen, setBulkWidgetQueryDialogIsOpen] = React.useState(false);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const handleClearWidgets = (status:boolean) => {
    setClearDialogOpen(false);
    if (status) {
      props.onClearWidgets();
    }
  }

  const handleCopyDashboard = async () => {
    if (dashboardId) {
      const requestParams = { "to_user": dashboardId };
      await APIRequest(`obj/dashboard/${dashboardId}/duplicate`, requestParams, { method: "POST" })
        .then((response: any) => {
          enqueueSnackbar(i18n.t("resource-editor.duplicate-successful"), { variant: "success" });
        }).catch((error: APIError) => {
          enqueueSnackbar(i18n.t("resource-editor.duplicate-error"), { variant: "error" });
          return error;
        });
    }
  };

  const handleExportImage = () => {
    const hasWidgets = props.widgets && props.widgets.length > 0;
    if (hasWidgets) {
      exportAsImage(...[props.imageExportRef, `${i18n.t('nav.dashboard')} - ${dashboard.name}`, "png", true, theme, didExportImage]);
    } else {
      enqueueSnackbar(i18n.t("widgets.download-image-failure-no-widgets"), { variant: 'warning', persist: true });
    }
  }

  const didExportImage = (success: boolean) => {
    if (success) {
      const embedList = props.widgets?.find((item: any) => item.name === "embed-list");
      if (embedList) {
        enqueueSnackbar(i18n.t("widgets.download-image-success-warning-embeds"), { variant: 'warning' });
      } else {
        enqueueSnackbar(i18n.t("widgets.download-image-success"), { variant: 'success' });
      }
    } else {
      enqueueSnackbar(i18n.t("widgets.download-image-failure"), { variant: 'error', persist: true });
    }
  }

  async function bulkSaveWidgetQueries(widgetQueries: WidgetQuery[]) {
    if (!widgets || widgets.length === 0 || !onUpdateWidget) {
      return;
    }

    const updatePromises = widgets.map(async (widget) => {
      const queriesToSubmit = widgetQueries?.length ? widgetQueries : undefined;
      const requestInfo = { ...widget.request_info };
      requestInfo.queries = queriesToSubmit;
      const updatedWidgetInfo = { request_info: requestInfo, cache: {} };
      return await onUpdateWidget(widget.id, updatedWidgetInfo, false);
    });

    await Promise.all(updatePromises);
  }

  return (
    <Box sx={{display:"inline-block", verticalAlign:"middle"}}>
      {!isMediumScreen && dashboard._modifiable &&
        <>
        {/* Open the add widget dialog */}
        <Button
          variant="contained"
          size="small"
          color='success'
          sx={{marginRight: "5px"}}
          startIcon={<PostAdd/>}
          onClick={() => { setWidgetDialogOpen(true); }}>
          {i18n.t("add")} {i18n.t("widget")}
        </Button>
        {/* Toggle edit mode */}
        <Button
          variant="outlined"
          size="small"
          sx={{marginRight: "5px"}}
          startIcon={<ControlCamera/>}
          onClick={() => { props.setIsInLayoutMode(!props.isInLayoutMode); }}
        >
          <>
          {props.isInLayoutMode && i18n.t("dashboard.view-mode")}
          {!props.isInLayoutMode && i18n.t("dashboard.layout-mode")}
          </>
        </Button>
        {/* Share dashboard */}
        <Button
          variant="outlined"
          size="small"
          sx={{marginRight: "5px"}}
          startIcon={<IosShare/>}
          onClick={(e) => {
            e.preventDefault();
            setShareDashboardDialogIsOpen(true);
          }}>
          {i18n.t("dashboard.share-dashboard")}
        </Button>
        </>
      }
      {!isSmallScreen && isMediumScreen && dashboard._modifiable &&
        <>
        <Tooltip title={i18n.t("add") + " " + i18n.t("widget")}>
          <IconButton
            color="success"
            onClick={() => { setWidgetDialogOpen(true); }}>
            <PostAdd />
          </IconButton>
        </Tooltip>
        <Tooltip title={props.isInLayoutMode ? i18n.t("dashboard.view-mode") : i18n.t("dashboard.layout-mode")}>
          <IconButton
            onClick={() => { props.setIsInLayoutMode(!props.isInLayoutMode); }}>
            <ControlCamera />
          </IconButton>
        </Tooltip>
        <Tooltip title={i18n.t("dashboard.share-dashboard")}>
          <IconButton
            aria-label={i18n.t("dashboard.share-dashboard")}
            onClick={(e) => {
              e.preventDefault();
              setShareDashboardDialogIsOpen(true);
              setShareDashboardDialogIsOpen(true);
            }}>
            <IosShare />
          </IconButton>
        </Tooltip>
        </>
      }
      <IconButton onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (actionMenuAnchorEl) {
            setActionMenuAnchorEl(null);
          } else {
            setActionMenuAnchorEl(event.currentTarget);
          }
        }}>
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={!!actionMenuAnchorEl}
        onClose={() => setActionMenuAnchorEl(null)}
      >
        {dashboard._modifiable &&
          <MenuItem onClick={() => { setWidgetDialogOpen(true); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <PostAdd color="success"/>
            </ListItemIcon>
            <ListItemText>
              {i18n.t("add")} {i18n.t("widget")}
            </ListItemText>
          </MenuItem>
        }
        {dashboard._modifiable &&
          <MenuItem onClick={() => { props.setIsInLayoutMode(!props.isInLayoutMode); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <ControlCamera/>
            </ListItemIcon>
            <ListItemText>
              {props.isInLayoutMode ? i18n.t("dashboard.view-mode") : i18n.t("dashboard.layout-mode")}
            </ListItemText>
          </MenuItem>
        }
        {dashboard._modifiable &&
          <MenuItem onClick={() => {
              setEditDashboardDialogIsOpen(true);
              setActionMenuAnchorEl(null);
            }}>
            <ListItemIcon>
              <Edit/>
            </ListItemIcon>
            <ListItemText>
              {i18n.t("dashboard.edit-dashboard-settings")}
            </ListItemText>
          </MenuItem>
        }
        {dashboard._modifiable &&
          <MenuItem onClick={() => { setShareDashboardDialogIsOpen(true); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <IosShare/>
            </ListItemIcon>
            <ListItemText>
              {i18n.t("dashboard.share-dashboard")}
            </ListItemText>
          </MenuItem>
        }
        {dashboard._modifiable &&
          <Divider />
        }
        {user?.permissions.includes("create_dashboard") &&
          <MenuItem onClick={() => { handleCopyDashboard(); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <CloudDownload />
            </ListItemIcon>
            <ListItemText>
              {i18n.t("duplicate")}
            </ListItemText>
          </MenuItem>
        }
        <MenuItem onClick={() => { handleExportImage(); setActionMenuAnchorEl(null); }}>
          <ListItemIcon>
            <Download/>
          </ListItemIcon>
          <ListItemText>
            {i18n.t("download")} {i18n.t("image")}
          </ListItemText>
        </MenuItem>
        {dashboard._modifiable &&
          <MenuItem onClick={() => { setBulkWidgetQueryDialogIsOpen(true); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <QueryStats />
            </ListItemIcon>
            <ListItemText>
              {i18n.t("dashboard.bulk-edit-widget-queries")}
            </ListItemText>
          </MenuItem>
        }
        {!dashboard.template_type && props.onRefreshWidgets && dashboard._modifiable &&
          <MenuItem onClick={() => { props.onRefreshWidgets(); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <Refresh/>
            </ListItemIcon>
            <ListItemText>
              {i18n.t("dashboard.refresh-widgets")}
            </ListItemText>
          </MenuItem>
        }
        {dashboard._modifiable &&
          <MenuItem onClick={() => { setClearDialogOpen(true); setActionMenuAnchorEl(null); }}>
            <ListItemIcon>
              <DeleteForever color="error" />
            </ListItemIcon>
            <ListItemText>
              {i18n.t("dashboard.clear-widgets")}
            </ListItemText>
          </MenuItem>
        }
      </Menu>
      {widgetDialogOpen &&
        <AddWidgetDialog
          onAddWidget={async (type:string, widgetOptions?:any, startingLayouts?:any, openSettingsOnAdd?: boolean) => {
            await props.onAddWidget(type, widgetOptions, startingLayouts, openSettingsOnAdd);
            props.setIsInLayoutMode(true);
          }}
          onClose={() => setWidgetDialogOpen(false)}
          open={widgetDialogOpen}
        />
      }
      <ConfirmationDialog
        isOpen={clearDialogOpen}
        title={i18n.t("dashboard.clear-widgets")}
        description={i18n.t("dashboard.clear-widgets-description")}
        onClose={handleClearWidgets}
      />
      {editDashboardDialogIsOpen &&
        <DashboardEditDialog
          setDialogIsOpen = {setEditDashboardDialogIsOpen}
          dashboardId = {dashboard.id}
          onClose = {(reason)=>{
            setEditDashboardDialogIsOpen(false);
            if (reason !== "dashboardDeleted") {
              // Reload the current page.
              const queryIdString = queryIds ?? "";
              if (queryIdString && queryIdString.length) {
                navigate(`/dashboard/${dashboardId}/${queryIdString}`, { replace: true });
              } else {
                window.location.reload();
              }
            } else {
              navigate("/start", { replace: true });
            }
          }}
          />
      }
      {shareDashboardDialogIsOpen &&
        <ResourceActionDialog
          open={shareDashboardDialogIsOpen}
          accountAccess={dashboard.access}
          action="share"
          resourceIds={dashboard.id}
          endpoint="dashboard"
          onClose={() => setShareDashboardDialogIsOpen(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setShareDashboardDialogIsOpen(false);
              enqueueSnackbar(i18n.t("resource-editor.save-successful"), { variant: "success" });
              window.location.reload();
            }
        }} />
      }
      {bulkWidgetQueryDialogIsOpen &&
        <WidgetMultiqueryDialog
          dashboardDatasetId={dashboard?.template_type ? (dashboard?.dataset_id || undefined) : undefined}
          queryIds={[]}
          onClose={() => {
            setBulkWidgetQueryDialogIsOpen(false);
          }}
          onSubmit={(widgetQueries: WidgetQuery[]) => {
            bulkSaveWidgetQueries(widgetQueries).then(() => {
              setTimeout(() => { 
                window.location.reload();
              }, 500);
            }).catch((error: APIError) => {
              enqueueAPIErrorSnackbar(error, enqueueSnackbar);
            });
            setBulkWidgetQueryDialogIsOpen(false);
          }}
        />
      }
    </Box>
  );
}
